import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import { CheckCircle2, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh } = useSubscription();
  const [state, setState] = useState({ status: "polling", attempts: 0 });

  useEffect(() => {
    const sessionId = new URLSearchParams(loc.search).get("session_id");
    if (!sessionId) { setState({ status: "error" }); return; }

    let cancelled = false;
    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "success" });
          return;
        }
      } catch { /* keep trying */ }
      if (attempt >= 12) { setState({ status: "timeout" }); return; }
      setState({ status: "polling", attempts: attempt });
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll(1);
    return () => { cancelled = true; };
  }, [loc.search, refresh]);

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-100 grain flex items-center justify-center p-6 relative">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-indigo-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 circa-card p-8 sm:p-10 max-w-md text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-indigo-300 mx-auto mb-4 animate-spin" />
            <h2 className="font-display text-2xl font-extrabold">Activating your Pro plan…</h2>
            <p className="text-slate-400 text-sm mt-2">Just a moment — confirming with Stripe.</p>
          </>
        )}
        {state.status === "success" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <div className="pill mx-auto mb-3 bg-emerald-950/60 border-emerald-500/40 text-emerald-200 w-fit">
              <Sparkles className="w-3.5 h-3.5" /> Welcome to CircaShift Pro
            </div>
            <h2 className="font-display text-2xl font-extrabold">You're all set</h2>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed">
              Your 7-day free trial has started. Unlock Jet Lag, Alertness, unlimited AI advisor, and every badge.
            </p>
            <Button
              onClick={() => nav("/dashboard")}
              data-testid="btn-go-dashboard"
              className="mt-6 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
            >
              Go to dashboard <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </>
        )}
        {(state.status === "timeout" || state.status === "error") && (
          <>
            <h2 className="font-display text-2xl font-extrabold">Confirming payment…</h2>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed">
              This usually settles in seconds. Head to the dashboard — your plan will refresh automatically.
            </p>
            <Button onClick={() => nav("/dashboard")} className="mt-6 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white">
              Continue
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
